'use client';
import { FC } from 'react';
import { EFinanceStateUpdateValue, InterestRate } from '@/types';
import { numberWithCommas, PMT } from '@/utils';
import classes from './styles.module.scss';
import { useTranslation, useWindowDimensions } from '@/hook';
import { FinancingResult } from '../../result';
import { useFormContext } from 'react-hook-form';
import TextFieldFormControlled from '@/components/shared/input/text-field/form-controlled';
import WrappedSlider from '@/components/shared/input/slider';
import WrappedSliderFormControlled from '@/components/shared/input/slider/form-controlled';
import Typography from '@/components/shared/typography/typography.component';
import colors from '@/theme/colors.module.scss';

const defaultMonths = 60;

interface ITabCarPrice {
    min: number;
    max: number;
    canApply?: boolean;
    eventName?: string;
    carName?: string;
    interestRates: InterestRate[];
    tenorCarPrice: number;
    setTenorCarPrice: React.Dispatch<React.SetStateAction<number>>;
    downPayment: number;
    setDownPayment: React.Dispatch<React.SetStateAction<number>>;
    tenorEMI: number;
    setTenorEMI: React.Dispatch<React.SetStateAction<number>>;
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    type: EFinanceStateUpdateValue;
    minDownpayment: number;
    maxDownpayment: number;
}

export const TabCarPrice: FC<ITabCarPrice> = ({
    min,
    max,
    interestRates,
    downPayment,
    tenorEMI,
    isModalOpen,
    setIsModalOpen,
    tenorCarPrice,
    setTenorCarPrice,
    type,
    minDownpayment,
    maxDownpayment,
}) => {
    const { IS_DESKTOP } = useWindowDimensions();
    const { t } = useTranslation();

    const {
        // formState: { errors },
        control,
    } = useFormContext();
    // const { trackEvent } = useAnalytics();

    return (
        <div className={classes.container}>
            <div
                style={{
                    gap: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Typography variant="subtitle2Medium" color={colors.gray700}>
                    {t('FinanceCalculator.equivalentMonthlyInstallment')}
                </Typography>

                <div>
                    <TextFieldFormControlled
                        control={control}
                        name="emi"
                        leadingText={t('EGP') as string}
                        numeric
                        key={'emi'}
                    />
                    <WrappedSliderFormControlled
                        name="emi"
                        aria-label="Equivalent Monthly Installment"
                        control={control}
                        valueLabelDisplay="on"
                        sliderLabel
                        step={1000}
                        min={1000}
                        max={PMT(0.2525, 12, max * 0.9)}
                        valueLabelFormat={value =>
                            t('EGP') + ' ' + numberWithCommas(Math.ceil(value))
                        }
                        //   style={styles.sliderBottom}
                    />
                </div>
            </div>
            <div style={{ marginBlock: '16px' }} />
            <div
                style={{ gap: '8px', display: 'flex', flexDirection: 'column' }}
            >
                <Typography
                    variant="subtitle2Medium"
                    style={{ lineHeight: '20px', color: '#344054' }}
                >
                    {t('Downpayment')}
                </Typography>

                <div>
                    <TextFieldFormControlled
                        control={control}
                        name="dpAmount"
                        leadingText={t('EGP') as string}
                        numeric
                        key={'dpAmount'}
                    />
                    <WrappedSliderFormControlled
                        sliderLabel
                        name="dpAmount"
                        aria-label="Down Payment Amount"
                        control={control}
                        valueLabelDisplay="on"
                        step={10000}
                        marks
                        min={(min * minDownpayment) / 100}
                        max={(max * maxDownpayment) / 100}
                        valueLabelFormat={value =>
                            t('EGP') + ' ' + numberWithCommas(Math.ceil(value))
                        }
                    />
                </div>
            </div>
            <div style={{ marginBlock: '16px' }} />
            <div
                style={{
                    gap: '40px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Typography
                    variant="subtitle2Medium"
                    style={{ lineHeight: '20px', color: '#344054', gap: '8px' }}
                >
                    {t('Loan Tenor')}
                </Typography>
                <div>
                    <WrappedSlider
                        aria-label="Months"
                        defaultValue={defaultMonths}
                        labelAbove
                        value={tenorCarPrice}
                        valueLabelDisplay="on"
                        step={12}
                        marks
                        min={12}
                        max={84}
                        onChange={(_, value) => {
                            //   fireFinancingEvent();
                            setTenorCarPrice(value as number);
                        }}
                        valueLabelFormat={value => (
                            <Typography variant={'captionMedium'}>
                                {value + t('Months')}
                            </Typography>
                        )}
                        //   style={styles.slider}
                    />
                </div>
            </div>

            {!IS_DESKTOP && (
                <div
                    style={{ borderTop: '1px solid #D0D5DD', width: '100%' }}
                />
            )}
            {!IS_DESKTOP && (
                <FinancingResult
                    min={min}
                    max={max}
                    interestRates={interestRates}
                    downPayment={downPayment}
                    tenorEMI={tenorEMI}
                    tenorCarPrice={tenorCarPrice}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    type={type}
                />
            )}
        </div>
    );
};
